import { useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { toast } from "react-toastify";
import ChartDataLabels from "chartjs-plugin-datalabels";
import moment from "moment";
import { Icon } from "@iconify/react";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";

import { DataGrid } from "@mui/x-data-grid";
import "./DashboardWhatsapp.css";
import { SeverityPill } from "./SeverityPill";
import Loader from "../Components/Loader";
import { ReactComponent as LegendACW } from "../Images/LegendACW.svg";
import { ReactComponent as LegendAUX } from "../Images/LegendAUX.svg";

import {
  getWABAAgentSummary,
  getWABABroadcastSummary,
  getWABACallPermSummary,
} from "../Store/Actions/DashboardAction";

const DashboardWhatsapp = () => {
  const [agentSummaryData, setAgentSummaryData] = useState(null);
  const [broadcastSummaryData, setBroadcastSummaryData] = useState([]);
  const [callPermData, setCallPermData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [statusCount, setStatusCount] = useState({});

  function fetchData() {
    (async () => {
      try {
        const [
          agentSummaryResponse,
          callPermSummaryResponse,
          broadcastSummaryResponse,
        ] = await Promise.all([
          getWABAAgentSummary(),
          getWABACallPermSummary(),
          getWABABroadcastSummary(),
        ]);

        if (agentSummaryResponse?.status) {
          setAgentSummaryData(agentSummaryResponse.data);

          setStatusCount(
            countAgentStatus(agentSummaryResponse.data.agent_waba_summary)
          );
        } else console.log("agent summary error", agentSummaryResponse?.error);

        if (callPermSummaryResponse?.status)
          setCallPermData(callPermSummaryResponse.data);
        else console.log("call perm error", callPermSummaryResponse?.error);

        if (broadcastSummaryResponse?.status)
          setBroadcastSummaryData(broadcastSummaryResponse.data);
        else
          console.log(
            "broadcast summary error",
            broadcastSummaryResponse?.error
          );
      } catch (err) {
        console.error("fetchData error:", err);
        toast.error(`Error fetching data`, { autoClose: 3000 });
      } finally {
        setIsLoading(false);
      }
    })();
  }

  useEffect(() => {
    // Fetch data on first render
    fetchData();

    // Fetch data every minute
    const interval = setInterval(() => {
      fetchData();
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  const columnsAgentSummary = [
    {
      field: "agent_code",
      minWidth: 150,
      headerName: "Agent Code",
      headerAlign: "center",
    },

    {
      field: "full_name",
      minWidth: 150,
      headerName: "Agent Name",
      headerAlign: "center",
    },

    {
      align: "center",
      field: "status",
      headerName: "Status",
      minWidth: 150,
      headerAlign: "center",

      renderCell: (params) => (
        <SeverityPill color={params.row.status.toLowerCase()}>
          {params.row.status}
        </SeverityPill>
      ),
    },

    {
      align: "right",
      field: "status_duration",
      minWidth: 150,
      headerName: "State Duration",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_all_time",
      minWidth: 150,
      headerName: "All Time",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_unread_all_time",
      minWidth: 150,
      headerName: "Unread All Time",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_unread_longest_received",
      minWidth: 180,
      headerName: "Longest Unread",
      headerAlign: "center",

      renderCell: (params) =>
        params.row.total_active_chat_unread_longest_received
          ? moment(params.row.total_active_chat_unread_longest_received).format(
              "DD-MMM-yyyy HH:mm:ss"
            )
          : null,
    },

    {
      align: "right",
      field: "total_active_chat_read_no_reply",
      minWidth: 200,
      headerName: "Read No Reply All Time",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_read_no_reply_longest_received",
      minWidth: 200,
      headerName: "Longest Read No Reply",
      headerAlign: "center",

      renderCell: (params) =>
        params.row.total_active_chat_read_no_reply_longest_received
          ? moment(
              params.row.total_active_chat_read_no_reply_longest_received
            ).format("DD-MMM-yyyy HH:mm:ss")
          : null,
    },

    {
      align: "right",
      field: "total_active_chat_interact_today",
      minWidth: 150,
      headerName: "Interact Today",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_marketing_delivered_today",
      minWidth: 150,
      headerName: "Marketing",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_utility_delivered_today",
      minWidth: 150,
      headerName: "Utility",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_active_chat_call_perm_delivered_today",
      minWidth: 150,
      headerName: "Call Perm",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_resolved_chat_today",
      minWidth: 150,
      headerName: "Today",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_resolved_chat_marketing_delivered_today",
      minWidth: 150,
      headerName: "Marketing",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_resolved_chat_utility_delivered_today",
      minWidth: 150,
      headerName: "Utility",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "total_resolved_chat_call_perm_delivered_today",
      minWidth: 150,
      headerName: "Call Perm",
      headerAlign: "center",
    },
  ];

  const columnAgentSummaryGroupingModel = [
    {
      groupId: "Active Chat",

      children: [
        { field: "total_active_chat_all_time" },
        { field: "total_active_chat_unread_all_time" },
        { field: "total_active_chat_unread_longest_received" },
        { field: "total_active_chat_read_no_reply" },
        { field: "total_active_chat_read_no_reply_longest_received" },
        { field: "total_active_chat_interact_today" },

        {
          groupId: "Template Delivered Today",

          children: [
            { field: "total_active_chat_marketing_delivered_today" },
            { field: "total_active_chat_utility_delivered_today" },
            { field: "total_active_chat_call_perm_delivered_today" },
          ],
        },
      ],
    },

    {
      groupId: "Resolved Chat",

      children: [
        { field: "total_resolved_chat_today" },

        {
          groupId: "Template Delivered Today",

          children: [
            { field: "total_resolved_chat_marketing_delivered_today" },
            { field: "total_resolved_chat_utility_delivered_today" },
            { field: "total_resolved_chat_call_perm_delivered_today" },
          ],
        },
      ],
    },
  ];

  const columnsBroadcastSummary = [
    {
      field: "broadcast_name",
      minWidth: 250,
      headerName: "Broadcast Name",
      headerAlign: "center",
    },

    {
      field: "template_name",
      minWidth: 250,
      headerName: "Template Name",
      headerAlign: "center",
    },

    {
      field: "table_name",
      headerName: "Table Name",
      minWidth: 200,
      headerAlign: "center",
    },

    {
      align: "right",
      field: "created_time",
      minWidth: 180,
      headerName: "Created Time",
      headerAlign: "center",

      renderCell: (params) =>
        params.row.created_time
          ? moment(params.row.created_time).format("DD-MMM-yyyy HH:mm:ss")
          : null,
    },

    {
      align: "right",
      field: "broadcast_time",
      minWidth: 180,
      headerName: "Broadcast Time",
      headerAlign: "center",

      renderCell: (params) =>
        params.row.broadcast_time
          ? moment(params.row.broadcast_time).format("DD-MMM-yyyy HH:mm:ss")
          : null,
    },

    {
      field: "broadcast_by_agent_code",
      minWidth: 225,
      headerName: "Broadcast by Agent Code",
      headerAlign: "center",
    },

    {
      field: "broadcast_by_full_name",
      minWidth: 200,
      headerName: "Broadcast by Full Name",
      headerAlign: "center",
    },

    {
      field: "status",
      minWidth: 100,
      headerName: "Status",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "recipient",
      minWidth: 100,
      headerName: "Recipient",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "sent",
      minWidth: 100,
      headerName: "Sent",
      headerAlign: "center",
    },

    {
      align: "right",
      field: "failed",
      minWidth: 100,
      headerName: "Failed",
      headerAlign: "center",
    },
  ];

  const columnsAllTemplateSummary = [
    {
      field: "templatename",
      flex: 1,
      headerName: "Template Name",
      headerAlign: "center",
    },

    {
      field: "category",
      flex: 0.4,
      headerName: "Category",
      headerAlign: "center",
    },

    {
      align: "center",
      field: "iscallpermission",
      headerName: "Call Permission",
      flex: 0.6,
      headerAlign: "center",

      renderCell: (params) =>
        params.row.iscallpermission !== null
          ? params.row.iscallpermission.toString().toUpperCase()
          : null,
    },

    {
      align: "right",
      field: "total_all_delivered_today",
      flex: 0.5,
      headerName: "Delivered Today",
      headerAlign: "center",
    },
  ];

  const dataAgentSummary = {
    labels: ["Ready", "Busy", "ACW", "AUX", "Not Bound"],

    datasets: [
      {
        data: Object.values(statusCount),

        backgroundColor: [
          "#00FF00",
          "#FF0000",
          "#FFE900",
          "#EE7314",
          "#727272",
        ],
      },
    ],
  };

  const optionsAgentSummary = {
    responsive: true,

    plugins: {
      datalabels: {
        font: {
          size: 16,
          weight: 700,
        },

        color: (context) => {
          return context.dataset.backgroundColor[context.dataIndex] ===
            "#00FF00" ||
            context.dataset.backgroundColor[context.dataIndex] === "#FFE900"
            ? "#393939"
            : "#FFF";
        },

        display: (context) => context.dataset.data[context.dataIndex] > 0,

        formatter: (value) => {
          const filteredAgents = agentSummaryData.agent_waba_summary
            ? agentSummaryData.agent_waba_summary.filter(
                (agent) =>
                  agent.status !== "OFFLINE" && agent.status !== "LOGGEDOFF"
              )
            : [];

          const countPercentage = (value, total) =>
            Math.round((100 * value) / total);

          return countPercentage(value, filteredAgents.length) + "%";
        },
      },

      legend: {
        display: false,
      },
    },
  };

  function countAgentStatus(data) {
    const filteredAgents = data
      ? data.filter(
          (agent) => agent.status !== "OFFLINE" && agent.status !== "LOGGEDOFF"
        )
      : [];

    const countStatus = (status) =>
      filteredAgents.reduce(
        (counter, obj) => (obj.status === status ? ++counter : counter),
        0
      );

    return {
      ready: countStatus("READY"),
      busy: countStatus("BUSY"),
      acw: countStatus("ACW"),
      aux: countStatus("AUX"),
      notBound: countStatus("NOT BOUND"),
    };
  }

  return (
    <div className="dashboard-whatsapp">
      <div className="list_header">
        <div className="list_headerLeft">
          <h1>Whatsapp</h1>
        </div>
      </div>

      <div className="containerDashboard">
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <section className="agent-cards">
              <TableContainer className="agent-card bottom-card">
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "80%" }}>
                        Total Active Chat All Time and Resolved Today
                      </TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.chat_waba_summary
                            .total_active_all_time_and_resolved_today
                        }
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>
                        Resolved Chat Today
                      </TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.chat_waba_summary
                            .total_resolved_chat_waba_today
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>
                        Active Chat All Time
                      </TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.chat_waba_summary
                            .total_active_chat_waba_all_time
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>
                        Active Chat Unread All Time
                      </TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.chat_waba_summary
                            .total_active_chat_unread_all_time
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>
                        Active Chat Read No Reply All Time
                      </TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.chat_waba_summary
                            .total_active_chat_read_no_reply_all_time
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ borderBottom: "none", width: "90%" }}>
                        Active Chat Interact Today
                      </TableCell>

                      <TableCell align="right" sx={{ borderBottom: "none" }}>
                        {
                          agentSummaryData.chat_waba_summary
                            .total_active_chat_interact_today
                        }
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <TableContainer className="agent-card bottom-card">
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ width: "80%" }}>
                        Template Message Delivered Today (Utility + Marketing)
                      </TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.template_waba_summary
                            .total_all_delivered_today
                        }
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>Utility</TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.template_waba_summary
                            .total_utility_delivered_today
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>Marketing</TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.template_waba_summary
                            .total_marketing_delivered_today
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ borderBottom: "none", width: "90%" }}>
                        Call Permission
                      </TableCell>

                      <TableCell align="right" sx={{ borderBottom: "none" }}>
                        {
                          agentSummaryData.template_waba_summary
                            .total_call_perm_delivered_today
                        }
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <TableContainer className="agent-card bottom-card">
                <Table aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell colSpan={2}>Today Message Summary</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>Sent</TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.today_message_summary
                            .total_message_sent_today
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>Delivered</TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.today_message_summary
                            .total_message_delivered_today
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ width: "90%" }}>Received</TableCell>

                      <TableCell align="right">
                        {
                          agentSummaryData.today_message_summary
                            .total_message_received_today
                        }
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell sx={{ borderBottom: "none", width: "90%" }}>
                        Received Unread
                      </TableCell>

                      <TableCell align="right" sx={{ borderBottom: "none" }}>
                        {
                          agentSummaryData.today_message_summary
                            .total_message_received_unread_today
                        }
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </section>

            <section className="agents-detail">
              <div className="agents-chart">
                <div style={{ height: 300 }}>
                  <Doughnut
                    data={dataAgentSummary}
                    options={optionsAgentSummary}
                    plugins={[ChartDataLabels]}
                  />
                </div>

                <div className="agents-chart-legend">
                  <div className="ready">
                    <Icon
                      height="26"
                      icon="tabler:square-rounded-check"
                      width="26"
                    />

                    <span>Ready</span>
                    <span className="count">{statusCount.ready}</span>
                  </div>

                  <div className="busy">
                    <Icon
                      height="26"
                      icon="tabler:alert-square-rounded-filled"
                      width="26"
                    />

                    <span>Busy</span>
                    <span className="count">{statusCount.busy}</span>
                  </div>

                  <div className="acw">
                    <LegendACW />
                    <span>ACW</span>
                    <span className="count">{statusCount.acw}</span>
                  </div>

                  <div className="aux">
                    <LegendAUX />
                    <span>AUX</span>
                    <span className="count">{statusCount.aux}</span>
                  </div>

                  <div className="not-bound">
                    <Icon
                      height="26"
                      icon="tabler:square-rounded-x"
                      width="26"
                    />

                    <span>Not Bound</span>
                    <span className="count">{statusCount.notBound}</span>
                  </div>
                </div>
              </div>

              <div className="item acd-agents">
                <header>
                  <div className="title">
                    <Icon
                      height="26"
                      icon="ic:round-support-agent"
                      width="26"
                    />

                    <p>Agent Summary</p>
                  </div>

                  <p className="amount">
                    {agentSummaryData.agent_waba_summary?.length}
                  </p>
                </header>

                <div style={{ height: 500 }}>
                  <DataGrid
                    className="table"
                    columnGroupingModel={columnAgentSummaryGroupingModel}
                    columns={columnsAgentSummary}
                    disableColumnFilter
                    disableColumnMenu
                    disableSelectionOnClick
                    experimentalFeatures={{ columnGrouping: true }}
                    hideFooter
                    rows={agentSummaryData.agent_waba_summary}
                    getRowId={(row) => row.agent_id}
                  />
                </div>
              </div>
            </section>

            <section className="other-summary">
              <div className="call-permission-and-template">
                <div className="item call-permission-summary">
                  <header>
                    <div className="title">
                      <p>Call Permission Summary</p>
                    </div>
                  </header>

                  <div className="call-permission-summary-table">
                    <div className="total-all">Total Active Chat All Time</div>
                    <div className="call-acc">Call Perm Accepted</div>
                    <div className="call-exp">Call Perm Expired Renewal</div>
                    <div className="call-rej">Call Perm Rejected</div>
                    <div className="call-init">Call Perm Initial</div>
                    <div className="call-acc" />
                    <div>Delivered All Time but No Response</div>
                    <div>Delivered Today but No Response</div>
                    <div>Not Sent</div>
                    <div className="call-rej" />
                    <div>Delivered All Time but No Response</div>
                    <div>Delivered Today but No Response</div>
                    <div>Not Sent</div>

                    <div className="call-acc values">
                      {callPermData.total_active_chat_call_perm_accepted}
                    </div>

                    <div className="values">
                      {
                        callPermData.total_active_chat_call_perm_expired_renewal_delivered_all_time
                      }
                    </div>

                    <div className="values">
                      {
                        callPermData.total_active_chat_call_perm_expired_renewal_delivered_today
                      }
                    </div>

                    <div className="values">
                      {
                        callPermData.total_active_chat_call_perm_expired_renewal_not_sent
                      }
                    </div>

                    <div className="call-rej values">
                      {callPermData.total_active_chat_call_perm_rejected}
                    </div>

                    <div className="values">
                      {
                        callPermData.total_active_chat_call_perm_initial_delivered_all_time_no_response
                      }
                    </div>

                    <div className="values">
                      {
                        callPermData.total_active_chat_call_perm_initial_delivered_today_no_response
                      }
                    </div>

                    <div className="values">
                      {
                        callPermData.total_active_chat_call_perm_initial_not_sent
                      }
                    </div>
                  </div>
                </div>

                <div className="item all-template-summary">
                  <header>
                    <div className="title">
                      <p>All Template Summary</p>
                    </div>
                  </header>

                  <div style={{ height: 408 }}>
                    <DataGrid
                      className="table"
                      columns={columnsAllTemplateSummary}
                      disableColumnFilter
                      disableColumnMenu
                      disableSelectionOnClick
                      hideFooter
                      rows={agentSummaryData.template_summary}
                      getRowId={(row) => row.templatename}
                    />
                  </div>
                </div>
              </div>

              <div className="item broadcast-summary">
                <header>
                  <div className="title">
                    <p>Broadcast Summary</p>
                  </div>
                </header>

                <div style={{ height: 500 }}>
                  <DataGrid
                    className="table"
                    columns={columnsBroadcastSummary}
                    disableColumnFilter
                    disableColumnMenu
                    disableSelectionOnClick
                    hideFooter
                    rows={broadcastSummaryData}
                    getRowId={(row) =>
                      `${row.broadcast_name}_${row.broadcast_time}`
                    }
                  />
                </div>
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default DashboardWhatsapp;
